import React from "react";
import Container from "react-bootstrap/Container";
import Table from "react-bootstrap/Table";
import {Link} from "react-router-dom";
import moment from "moment";

import JwtClient from "./JwtClient";
import Logout from "./utils";
import Alert from "./Alert";

const pageSize = 50;
// One UPS Ground label takes a few seconds, a batch is created one after another.
const postTimeoutMs = 10 * 60 * 1000;
const defaultSecondsPerLabel = 4;
const batchPageSize = 20;

const formatDuration = (seconds) => {
    const s = Math.max(0, Math.round(seconds));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

export default class UPSAdultRelabel extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            user: 'NovaPoshta',
            idsText: '',
            manifestID: '',
            confirming: false, // the submit button was clicked once
            submitting: false,
            alert: '',
            results: [],
            relabels: [],
            offset: 0,
            hasMore: false,
            loading: true,
            batches: [],
            batchOffset: 0,
            batchHasMore: false,
            secondsPerLabel: null, // from the latest batches, null when unknown
            batchID: '', // the batch just relabeled
            deadline: 0, // ms, when the relabel is expected to be done
            now: Date.now(),
        };
    }

    timer = null;
    perLabel = () => this.state.secondsPerLabel || defaultSecondsPerLabel;

    ids = () => this.state.idsText.split(/\s+/).filter(h => h !== '');

    render() {
        const count = this.ids().length;
        const canSubmit = this.state.user !== '' && count > 0 && this.state.manifestID.trim() !== '' && !this.state.submitting;
        return (
            <Container>
                <h4 className="mt-4">Relabel to UPS Ground with Adult Signature Required</h4>
                <p className="text-muted">
                    Packages are found by HAWB, tracking number or auxiliary tracking number. Each package gets a new UPS Ground label with adult signature required and the same HAWB.
                    The new package is put into the manifest below. The old package is only marked as deleted,
                    its UPS label is not voided. A package that has tracking events (left the warehouse) or
                    was already relabeled is refused. Only packages of the chosen user are relabeled and the manifest must
                    belong to this user too. A list with another user's package is refused as a whole.
                </p>

                {this.state.alert ? <Alert>{this.state.alert}</Alert> : null}

                <form onSubmit={this.handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="user">User</label>
                        <select id="user" className="form-control"
                                value={this.state.user}
                                disabled={this.state.submitting}
                                onChange={e => this.setState({user: e.currentTarget.value, confirming: false})}>
                            <option value="NovaPoshta">NovaPoshta</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label htmlFor="ids">HAWBs or tracking numbers <span className="text-muted">({count}), separated by spaces or new lines</span></label>
                        <textarea id="ids" className="form-control" rows="6"
                                  value={this.state.idsText}
                                  disabled={this.state.submitting}
                                  onChange={e => this.setState({idsText: e.currentTarget.value, confirming: false})}/>
                    </div>
                    <div className="form-group">
                        <label htmlFor="manifest">Manifest ID</label>
                        <input id="manifest" className="form-control"
                               placeholder="00000000-0000-0000-0000-000000000000"
                               value={this.state.manifestID}
                               disabled={this.state.submitting}
                               onChange={e => this.setState({manifestID: e.currentTarget.value, confirming: false})}/>
                    </div>
                    <button type="submit" disabled={!canSubmit}
                            className={this.state.confirming ? "btn btn-danger" : "btn btn-primary"}>
                        {this.state.submitting ?
                            <span><span className="spinner-border spinner-border-sm mr-2"></span>{this.renderCountdown()}</span> :
                            this.state.confirming ?
                                `Click again to relabel ${count} package(s) of ${this.state.user} to UPS Ground, adult signature (takes about ${formatDuration(count * this.perLabel())})` :
                                'Relabel'}
                    </button>
                    {this.state.confirming && !this.state.submitting ?
                        <button type="button" className="btn btn-link"
                                onClick={() => this.setState({confirming: false})}>Cancel</button> : null}
                </form>

                {this.renderResults()}
                {this.renderBatches()}
                {this.renderHistory()}
            </Container>
        )
    }

    renderResults() {
        if (this.state.results.length === 0) {
            return null;
        }
        return (
            <div className="mt-4">
                <h5>Result</h5>
                {this.state.batchID ? this.renderBatchButtons(this.state.batchID) : null}
                <Table size="sm">
                    <thead>
                    <tr>
                        <th>ID</th>
                        <th>HAWB</th>
                        <th>Status</th>
                        <th>New track / error</th>
                    </tr>
                    </thead>
                    <tbody>
                    {this.state.results.map(r => (
                        <tr key={r.id}>
                            <td>{r.id}</td>
                            <td>{r.hawb}</td>
                            <td className={r.status === 'ok' ? 'text-success' : 'text-danger'}>{r.status}</td>
                            <td>{r.status === 'ok' ?
                                <Link to={"/packages/" + r.new_tracking_number}>{r.new_tracking_number}</Link> :
                                r.error}</td>
                        </tr>
                    ))}
                    </tbody>
                </Table>
            </div>
        )
    }

    renderCountdown() {
        const left = (this.state.deadline - this.state.now) / 1000;
        if (left <= 0) {
            return 'Relabeling... taking longer than estimated';
        }
        return `Relabeling... about ${formatDuration(left)} left`;
    }

    renderBatchButtons(batchID) {
        const base = `/api/v1/admin/ups-adult-relabel-batches/${batchID}`;
        return (
            <span>
                <button type="button" className="btn btn-sm btn-outline-secondary mr-2"
                        onClick={() => this.downloadFile(`${base}/report.csv`, `ups-relabel-${batchID.slice(0, 8)}.csv`)}>CSV
                </button>
                <button type="button" className="btn btn-sm btn-outline-secondary"
                        onClick={() => this.downloadFile(`${base}/labels.zip`, `ups-relabel-labels-${batchID.slice(0, 8)}.zip`)}>Labels ZIP
                </button>
            </span>
        )
    }

    renderBatches() {
        if (this.state.batches.length === 0) {
            return null;
        }
        return (
            <div className="mt-4">
                <h5>Batches</h5>
                <Table size="sm">
                    <thead>
                    <tr>
                        <th>Date</th>
                        <th>Packages</th>
                        <th>Manifest</th>
                        <th>Download</th>
                    </tr>
                    </thead>
                    <tbody>
                    {this.state.batches.map(b => (
                        <tr key={b.batch_id}>
                            <td>{moment(b.started_at).format('MM/DD/YY h:mm a')}</td>
                            <td>{b.count}</td>
                            <td>{b.manifest_id ?
                                <Link to={"/manifests/" + b.manifest_id}>{b.manifest_mawb || b.manifest_id}</Link> : null}</td>
                            <td>{this.renderBatchButtons(b.batch_id)}</td>
                        </tr>
                    ))}
                    </tbody>
                </Table>
                <div className="d-flex justify-content-between">
                    <button className="btn btn-outline-secondary" disabled={this.state.batchOffset === 0}
                            onClick={() => this.getBatches(Math.max(0, this.state.batchOffset - batchPageSize))}>Newer
                    </button>
                    <button className="btn btn-outline-secondary" disabled={!this.state.batchHasMore}
                            onClick={() => this.getBatches(this.state.batchOffset + batchPageSize)}>Older
                    </button>
                </div>
            </div>
        )
    }

    renderHistory() {
        return (
            <div className="mt-4 mb-4">
                <h5>History</h5>
                {this.state.loading ?
                    <div className="d-flex justify-content-center">
                        <div className="spinner-border" role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                    </div> :
                    this.state.relabels.length === 0 ? <p>No relabels yet</p> :
                        <Table>
                            <thead>
                            <tr>
                                <th>Date</th>
                                <th>HAWB</th>
                                <th>Old track</th>
                                <th>Old aux track</th>
                                <th>New track</th>
                                <th>Manifest</th>
                            </tr>
                            </thead>
                            <tbody>
                            {this.state.relabels.map(r => (
                                <tr key={r.new_tracking_number}>
                                    <td>{moment(r.created_at).format('MM/DD/YY h:mm a')}</td>
                                    <td>{r.hawb}</td>
                                    <td><Link to={"/packages/" + r.old_tracking_number}>{r.old_tracking_number}</Link></td>
                                    <td>{r.old_auxiliary_tracking_number}</td>
                                    <td><Link to={"/packages/" + r.new_tracking_number}>{r.new_tracking_number}</Link></td>
                                    <td>{r.manifest_id ?
                                        <Link to={"/manifests/" + r.manifest_id}>{r.manifest_mawb || r.manifest_id}</Link> : null}</td>
                                </tr>
                            ))}
                            </tbody>
                        </Table>
                }
                <div className="d-flex justify-content-between">
                    <button className="btn btn-outline-secondary" disabled={this.state.offset === 0 || this.state.loading}
                            onClick={() => this.getHistory(Math.max(0, this.state.offset - pageSize))}>Newer
                    </button>
                    <button className="btn btn-outline-secondary" disabled={!this.state.hasMore || this.state.loading}
                            onClick={() => this.getHistory(this.state.offset + pageSize)}>Older
                    </button>
                </div>
            </div>
        )
    }

    componentDidMount() {
        this.getHistory(0);
        this.getBatches(0);
    }

    componentWillUnmount() {
        this.stopTimer();
    }

    stopTimer() {
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = null;
        }
    }

    getBatches(offset) {
        JwtClient.get(`/api/v1/admin/ups-adult-relabel-batches?offset=${offset}&limit=${batchPageSize + 1}`)
            .then(response => {
                const rows = response.data.batches;
                this.setState({
                    batches: rows.slice(0, batchPageSize),
                    batchHasMore: rows.length > batchPageSize,
                    batchOffset: offset,
                    secondsPerLabel: response.data.seconds_per_label,
                });
            })
            .catch(err => this.showError(err, 'Error on fetching batches'));
    }

    downloadFile = (url, name) => {
        JwtClient.get(url, {responseType: 'blob', timeout: 120000})
            .then(response => {
                const href = URL.createObjectURL(response.data);
                const a = document.createElement('a');
                a.href = href;
                a.download = name;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(href);
            })
            .catch(err => this.showError(err, 'Error on downloading ' + name));
    };

    // One more row than the page is asked for to know whether there is an older page.
    getHistory(offset) {
        this.setState({loading: true});
        JwtClient.get(`/api/v1/admin/ups-adult-relabels?offset=${offset}&limit=${pageSize + 1}`)
            .then(response => {
                const rows = response.data.relabels;
                this.setState({
                    relabels: rows.slice(0, pageSize),
                    hasMore: rows.length > pageSize,
                    offset: offset,
                });
            })
            .catch(err => this.showError(err, 'Error on fetching history'))
            .finally(() => this.setState({loading: false}));
    }

    handleSubmit = (e) => {
        e.preventDefault();
        if (!this.state.confirming) {
            this.setState({confirming: true});
            return
        }
        const expectedMs = this.ids().length * this.perLabel() * 1000;
        this.stopTimer();
        this.setState({
            submitting: true, confirming: false, alert: '', results: [], batchID: '',
            deadline: Date.now() + expectedMs, now: Date.now(),
        });
        this.timer = setInterval(() => this.setState({now: Date.now()}), 1000);
        JwtClient.post('/api/v1/admin/ups-adult-relabels', {
            user: this.state.user,
            ids: this.ids(),
            manifest_id: this.state.manifestID.trim(),
        }, {timeout: postTimeoutMs})
            .then(response => {
                const results = response.data.results;
                // Failed HAWBs stay in the textarea to be fixed and sent again.
                const failed = results.filter(r => r.status !== 'ok').map(r => r.id);
                this.setState({results: results, batchID: response.data.batch_id, idsText: failed.join('\n')});
                this.getHistory(0);
                this.getBatches(0);
            })
            .catch(err => this.showError(err, 'Error on relabeling'))
            .finally(() => {
                this.stopTimer();
                this.setState({submitting: false});
            });
    };

    showError(err, prefix) {
        if (!err.response) {
            this.setState({alert: JSON.stringify(err)});
            return
        }
        if (err.response.status === 401) {
            Logout();
            return
        }
        const detail = err.response.data && err.response.data.error ? ': ' + err.response.data.error : '';
        this.setState({alert: `${prefix}, status: ${err.response.status}${detail}`});
    }
}
